/**
 * Builds the `shape-outside` for a block whose right side is covered by one or
 * more front blocks (the nested/cascade overlap). Each covering block is
 * "right-anchored" — it extends from its left inset to the right edge — so the
 * covered region at any vertical position is `[boundary(y), right]`, where
 * `boundary(y)` is the LEFTMOST inset among the covers active at y. That makes
 * the covered area a right-anchored STAIRCASE, not a single rectangle.
 *
 * We carve that staircase out with a `polygon()` so text flows into the free
 * area to its left at every band (and full-width in vertical gaps / below it).
 * A single `inset()` rectangle (the old approach) used the min inset for the
 * whole height, so a block covered by a far-right block at the top and a
 * less-inset block lower down lost its whole top-left area and got its title
 * pushed below — the bug this fixes.
 *
 * Pure + deterministic → unit tested.
 */

export interface CoverRect {
  /** Left inset of the cover, px, relative to this block's content origin. */
  leftPx: number;
  topPx: number;
  bottomPx: number;
}

/** A free strip narrower than this isn't worth flowing text into — that band is
 *  treated as fully covered so text skips it (flows above/below instead). */
const MIN_FLOW_W = 56;

export interface CoverShape {
  /** CSS `shape-outside` (and clip) polygon value. */
  shape: string;
  /** Float height in px (covers don't reserve anything below this). */
  heightPx: number;
}

/** Returns the polygon shape + float height, or null when nothing is covered. */
export function coverShape(rects: CoverRect[], widthPx: number): CoverShape | null {
  if (rects.length === 0 || widthPx <= 0) return null;

  const ys = new Set<number>([0]);
  for (const r of rects) {
    ys.add(Math.max(0, r.topPx));
    ys.add(Math.max(0, r.bottomPx));
  }
  const breaks = [...ys].sort((a, b) => a - b);

  interface Band { y0: number; y1: number; left: number }
  const bands: Band[] = [];
  for (let i = 0; i < breaks.length - 1; i++) {
    const y0 = breaks[i]!, y1 = breaks[i + 1]!;
    if (y1 <= y0) continue;
    const mid = (y0 + y1) / 2;
    let left = widthPx; // uncovered ⇒ zero-width solid (text flows full width)
    for (const r of rects) {
      if (r.topPx <= mid && r.bottomPx >= mid) left = Math.min(left, Math.max(0, r.leftPx));
    }
    bands.push({ y0, y1, left });
  }

  // Skipping a narrow free strip (treating it as fully covered so text flows
  // elsewhere) only helps when there's a roomier band to flow into. When EVERY
  // band is narrowly covered (e.g. 3+ same-start events sharing the column, each
  // left with a thin strip), zeroing them would hide all content and leave the
  // block blank — so we keep the real (thin) boundary and at least show the title.
  const hasRoomElsewhere = bands.some((b) => b.left >= MIN_FLOW_W);
  if (hasRoomElsewhere) {
    for (const b of bands) if (b.left > 0 && b.left < MIN_FLOW_W) b.left = 0;
  }

  // Trailing uncovered bands reserve nothing — shrink the float to the last cover.
  while (bands.length && bands[bands.length - 1]!.left >= widthPx) bands.pop();
  if (bands.length === 0) return null;

  // Merge vertically-adjacent bands with the same boundary.
  const merged: Band[] = [];
  for (const b of bands) {
    const last = merged[merged.length - 1];
    if (last && last.left === b.left) last.y1 = b.y1;
    else merged.push({ ...b });
  }
  const heightPx = merged[merged.length - 1]!.y1;

  // Trace the solid (covered) region clockwise: top-right → down the left
  // staircase → bottom-right → up the right edge.
  const pts: Array<[number, number]> = [[widthPx, 0], [merged[0]!.left, 0]];
  for (let i = 0; i < merged.length; i++) {
    const b = merged[i]!;
    pts.push([b.left, b.y1]);
    const next = merged[i + 1];
    if (next) pts.push([next.left, b.y1]);
  }
  pts.push([widthPx, heightPx]);

  const poly = pts.map(([x, y]) => `${Math.round(x)}px ${Math.round(y)}px`).join(", ");
  return { shape: `polygon(${poly})`, heightPx };
}
